// Copied and modified: packages/components/account-subscriptions/billing-v2/invoices/src/components/invoice-page.tsx
import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import { AnalyticsEventSource, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import {
  FetchInvoicesTotalCountForTransactionAccountQuery,
  FetchInvoicesTotalCountForTransactionAccountQueryVariables,
  Maybe,
  PaymentMethodType,
  PaymentOnTerms,
  Scalars,
  PaymentMethod,
  TermPaymentTypes,
  Ach,
} from '@css-xp/graphtypes/src/generated-types-new';
import { FetchTransactionAccountQuery, TransactionAccount } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ExportJSONTypes, verifyJsonById } from '@atlassian/temp-proxies';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  useFeatureToggle,
  withApollo,
  FormattedDate,
  useRecentlyViewed,
} from '@css-xp/base';
import {
  fetchInvoiceQueryV2,
  getInvoiceSubheader,
  getTotalAmountPaidLabel,
  InvoiceWithLineItemsPPC,
} from '@css-xp/billing-v2';
import { fetchInvoiceQueryInPPC } from './queries/fetch-invoice-query-in-ppc';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import {
  PPC_PAGE_TITLE,
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  RecentlyViewedEntityEnum,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import {
  getEntityName,
  getRouteUrl,
  isPaypalPaymentMethod,
  isAchPaymentMethod,
  doesInvoiceHasPaymentIssue,
} from '@css-xp/ppc-utils';
import { useFetchTransactionAccountQuery } from '@css-xp/ppc-api';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { StatelessInvoiceLineItems } from '@css-xp/billing-v2/invoices/src/components/invoice-line-items';
import InvoiceViewAdjustmentPane from '@css-xp/billing-v2/invoices/src/components/invoice-view-adjustments-pane';
import { ViewPricing } from '@css-xp/base/common-sidebars';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import {
  ScreenEventAnalytics,
  InfoSection,
  SoldToCompanyName,
  PaymentFailureBanner,
  FormatCurrency,
} from '@css-xp/ppc-common';
import { DownloadJSONBanner } from '@css-xp/ppc-download-json-banner';
import { InvoiceDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import { IncludedProductsSidebar } from '@css-xp/ppc-subscription-table';
import { InvoiceDetailSummarySection, InvoiceMoreActions } from './components/invoice-details-summary-section';
import { BillingProfileDetailsSection } from './components/billing-profile-details-section';
import { fetchInvoicesTotalCountForTransactionAccount } from './queries/fetch-invoices-total-count-for-transaction-account';
import { InvoiceCustomerContact } from './invoice-customer-contact';
import './invoice-details-page.scss';

export enum InvoiceSearchParams {
  DOWNLOAD_JSON = 'downloadJson',
}

export enum SIDEBAR {
  VIEW_PRICING_DETAILS = 'viewPricingDetails',
  INVOICE_VIEW_ADJUSTMENTS = 'invoice-view-adjustments',
  INCLUDED_PRODUCTS = 'included-products',
}

type ExtendedInvoiceWithLineItems = InvoiceWithLineItemsPPC & {
  totalCreditLimit?: Maybe<Scalars['Float']>;
  onTermPaymentMethod?: Maybe<Scalars['String']>;
};

/* istanbul ignore next */
const getTransactionAccount = (queryData: FetchTransactionAccountQuery) => {
  return queryData?.transactionAccount as TransactionAccount;
};

export const InvoiceDetailsPage = (): JSX.Element | null => {
  const [verifyJSONRes, setVerifyJSONRes] = useState<{ isExists?: boolean; isLoading?: boolean }>();
  const [isJsonDownloaded, setIsJsonDownloaded] = useState(false);
  const { invoiceId, txaId } = useParams<{ invoiceId: string; txaId: string }>();
  const transactionAccountPageResult = useFetchTransactionAccountQuery({ txaId });
  const history = useHistory();
  useTitle(invoiceId, PPC_PAGE_TITLE.INVOICES);
  const [isEnabled] = useFeatureToggle();
  const { updateRecentlyViewed } = useRecentlyViewed();
  const isAddOnsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);

  /* Note: The fetchInvoiceQueryV2 does not restrict the results based on TXA. One approach was to
   * fetch invoices from a transaction account service. However, this led to missing data in
   * the invoice results. Moreover, includeItems search params was needed to get the line items
   * which required the type for the searchParams in query to change. This required the type to
   * change in all the places associated with TXA resolver/service. Then other related queries started
   * to fail. A major change. Hence, using the approach to get invoices total count for validation.
   */
  const { data: validInvoiceData, loading: isInvoicesTotalCountLoading, error: invoicesTotalCountError } = useQuery<
    FetchInvoicesTotalCountForTransactionAccountQuery,
    FetchInvoicesTotalCountForTransactionAccountQueryVariables
  >(fetchInvoicesTotalCountForTransactionAccount, {
    variables: {
      id: txaId,
      filterOptions: {
        and: [
          {
            or: [{ id: invoiceId }, { invoiceNumber: invoiceId }],
          },
        ],
        not: [{ status: 'DRAFT' }],
      },
    },
    displayName: 'fetchInvoicesForTransactionAccount',
  });
  const invoicesTotalCount = validInvoiceData?.transactionAccount?.invoices?.totalCount;
  const hasNoInvoicesForTXA = invoicesTotalCount === 0;
  const availablePaymentMethods = validInvoiceData?.transactionAccount?.availablePaymentMethods?.nodes;
  const deferredPaymentMethod = validInvoiceData?.transactionAccount?.availablePaymentMethods?.nodes?.filter(
    (paymentMethod: PaymentMethod | null) => paymentMethod?.type === PaymentMethodType?.DEFERRED
  ) as PaymentOnTerms[];
  const hasPayByStatement =
    deferredPaymentMethod && deferredPaymentMethod?.length > 0
      ? get(deferredPaymentMethod[0], 'terms.payOnStatement')
      : false;

  if (hasNoInvoicesForTXA) {
    // Redirect to not found page if no invoice is found for the TXA
    history.push(routePaths.notFound);
  }

  const isLoomOverageEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_LOOM_OVERAGE);

  const queryForFetchingInvoiceDetails = isLoomOverageEnabled ? fetchInvoiceQueryInPPC : fetchInvoiceQueryV2;
  const { data, loading, error } = useQuery(queryForFetchingInvoiceDetails, {
    variables: {
      id: invoiceId,
      transactionAccountId: txaId,
      ...(isLoomOverageEnabled && { includeOverage: isLoomOverageEnabled }),
      ...(isAddOnsEnabled && { fetchAddOns: isAddOnsEnabled, fetchAGPAddon: isAddOnsEnabled }),
    },
    fetchPolicy: 'no-cache',
    skip: hasNoInvoicesForTXA || !!invoicesTotalCountError,
  });

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isPaypalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isAchEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);

  useEffect(() => {
    if (data?.invoice?.id) {
      const checkIsJSONAvailable = async () => {
        try {
          setVerifyJSONRes({ isLoading: true });
          const res = await verifyJsonById({
            type: ExportJSONTypes.INVOICE,
            id: data?.invoice?.id,
            transactionAccountId: txaId,
          });
          setVerifyJSONRes({ isExists: res.isJSONAvailable, isLoading: false });
        } catch {
          setVerifyJSONRes({ isLoading: false });
        }
      };
      checkIsJSONAvailable();
    }
  }, [data?.invoice?.id]);

  useEffect(() => {
    updateRecentlyViewed({
      entityName: getEntityName(RecentlyViewedEntityEnum.INVOICE, txaId),
      metadata: {
        id: invoiceId,
        invoiceNumber: data?.invoice?.invoiceNumber,
        companyName: data?.invoice?.billTo?.fullName,
      },
    });
  }, [data]);

  const shouldSkipFetchInHooks =
    isInvoicesTotalCountLoading || hasNoInvoicesForTXA || !!invoicesTotalCountError || !!error || loading || !data;

  if (loading || isInvoicesTotalCountLoading || verifyJSONRes?.isLoading) {
    if (isDesignIteration1Enabled) {
      const shouldHideRefunds = isEmpty(data?.invoice?.refunds);

      return <InvoiceDetailsPageLoader shouldHideRefunds={shouldHideRefunds} />;
    }

    return <LoadingIndicator />;
  }

  if (error || invoicesTotalCountError) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);

    return <GenericError error={error || invoicesTotalCountError} />;
  }

  if (!data) {
    const NotFound = withErrorWrapper(ErrorComponents.NotFound);

    return <NotFound />;
  }

  const invoice = data.invoice as InvoiceWithLineItemsPPC;

  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, { txaId: txaId || '' });
  const refundsRoutePPC = getRouteUrl(routePaths.refunds, { txaId: txaId || '' });
  const cloudSiteRoutePPC = getRouteUrl(routePaths.cloudSite, { txaId: txaId || '', cloudSiteId: '' });

  const getInvoiceDetails = () => {
    const modifiedInvoice = data?.invoice as ExtendedInvoiceWithLineItems;
    const selectedPaymentMethod = availablePaymentMethods?.find(
      (paymentMethod: PaymentMethod | null) => paymentMethod?.id === modifiedInvoice.paymentMethodId
    );
    if (selectedPaymentMethod?.type === PaymentMethodType.DEFERRED) {
      const paymentOnTerms = selectedPaymentMethod as PaymentOnTerms;

      let invoiceTermPaymentMethod = null;
      if (paymentOnTerms?.terms?.invoiceDueDays === 14) {
        invoiceTermPaymentMethod = TermPaymentTypes.NET14;
      } else if (paymentOnTerms?.terms?.invoiceDueDays === 30) {
        invoiceTermPaymentMethod = TermPaymentTypes.NET30;
      }

      (modifiedInvoice.onTermPaymentMethod = invoiceTermPaymentMethod ?? paymentOnTerms?.paymentMethod),
        (modifiedInvoice.totalCreditLimit = paymentOnTerms?.totalCreditLimit);
      return modifiedInvoice;
    }
    if (isPaypalEnabled && isPaypalPaymentMethod(selectedPaymentMethod?.type)) {
      return {
        ...invoice,
        paypalAccountEmail: (selectedPaymentMethod as any)?.payPal?.email,
      };
    }
    if (isAchEnabled && isAchPaymentMethod(selectedPaymentMethod?.type)) {
      return {
        ...invoice,
        ach: (selectedPaymentMethod as any)?.ach as Ach,
      };
    }
    return invoice;
  };

  const getPageHeading = () => {
    return (
      <div className="page-heading">
        {isDesignIteration1Enabled ? invoice.invoiceNumber : <h1>{invoice.invoiceNumber}</h1>}
        {getInvoiceSubheader(invoice, true, deferredPaymentMethod)}
      </div>
    );
  };

  const renderLineItems = (): JSX.Element | undefined => {
    return (
      <StatelessInvoiceLineItems
        txaId={txaId}
        invoice={invoice}
        entitlementBaseRoute={subscriptionsRoutePPC}
        isPaymentMethodDetailsHidden={true}
        refundsBaseRoute={refundsRoutePPC}
        shouldDisableRefundsSidebar={true}
        cloudSiteBaseRoute={cloudSiteRoutePPC}
        renderCustomerContacts={(entitlementId: string) => (
          <InvoiceCustomerContact entitlementId={entitlementId} transactionAccountId={txaId} />
        )}
        isCustomerContactEnabled={false}
        isSortLineItemsEnabled={true}
        isSupportForOverageEnabled={isLoomOverageEnabled}
        shouldGroupAddOns={isAddOnsEnabled}
        isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
        isTWCPhase1Enabled={isTWCPhase1Enabled}
      />
    );
  };

  const allowedSidebars = {
    [SIDEBAR.VIEW_PRICING_DETAILS]: withApollo(ViewPricing),
    [SIDEBAR.INVOICE_VIEW_ADJUSTMENTS]: InvoiceViewAdjustmentPane,
    [SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
  };

  const renderActions = () => {
    return (
      <InvoiceMoreActions
        invoice={invoice}
        transactionAccountId={txaId}
        isJSONAvailable={verifyJSONRes?.isExists}
        isVerifyJSONLoading={verifyJSONRes?.isLoading}
        updateIsJsonDownloaded={setIsJsonDownloaded}
        {...(isDesignIteration1Enabled ? { appearance: 'default', trigger: 'Manage' } : {})}
      />
    );
  };

  const renderDownloadJSONBanner = () => {
    return (
      <DownloadJSONBanner
        id={invoice?.id}
        transactionAccountId={txaId}
        isJsonAvailable={!!verifyJSONRes?.isExists}
        type={ExportJSONTypes.INVOICE}
        isJsonDownloaded={isJsonDownloaded}
        analyticsForDownloadedAction={{
          trackSubject: AnalyticsTrackSubject.INVOICE_JSON,
          attributes: {
            invoiceId: invoice?.id,
          },
        }}
      />
    );
  };

  const renderInfoSection = (invoiceDetails: InvoiceWithLineItemsPPC) => {
    if (!isDesignIteration1Enabled) {
      return null;
    }

    // Logic same as:
    // 1. partner-purchase-center/pages/invoices/src/components/invoice-details-summary-section/invoice-details-summary-section.tsx
    // 2. account-subscriptions/billing-v2/invoices/src/components/invoice-amount-details.tsx
    return (
      <>
        <InfoSection
          items={[
            {
              label: 'Issued',
              value: <FormattedDate date={invoice?.createdTimestamp} />,
            },
            ...(!hasPayByStatement
              ? [
                  {
                    label: sentenceCase(getTotalAmountPaidLabel(invoiceDetails?.paymentMethod, invoiceDetails?.status)),
                    value: <FormattedDate date={invoice.dueDate} />,
                  },
                ]
              : []),
            {
              label: 'Customer',
              value: (
                <SoldToCompanyName txaId={txaId} companyName={invoice?.shipTo?.fullName} shouldRemoveLineClamp={true} />
              ),
            },
            {
              label: `Total amount ${getTotalAmountPaidLabel(invoiceDetails?.paymentMethod, invoiceDetails?.status)}`,
              value: (
                <p className="total-amount-value">
                  <FormatCurrency
                    showCurrencyCode
                    showCommas
                    currency={
                      isMultiCurrencyChangesEnabled ? invoiceDetails?.pricingCurrency : invoiceDetails?.paymentCurrency
                    }
                    value={toNumber(invoiceDetails?.total) + (invoiceDetails?.appliedBalance ?? 0)}
                  />
                </p>
              ),
              isRightAligned: true,
            },
          ]}
        />
        <br />
      </>
    );
  };

  const renderBillingProfileSection = (invoiceDetails: InvoiceWithLineItemsPPC) => {
    if (!isDesignIteration1Enabled) {
      return null;
    }

    return (
      <>
        <br />
        <BillingProfileDetailsSection
          invoice={invoiceDetails}
          hasPayByStatement={hasPayByStatement}
          transactionAccountId={txaId}
          invoiceGroupId={data?.invoice?.billingGroupId}
          isPaypalEnabled={isPaypalEnabled}
          isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
          shouldShowACHPaymentMethod={isAchEnabled}
        />
      </>
    );
  };

  const renderPaymentFailureBanner = (invoice: InvoiceWithLineItemsPPC) => {
    if (!isDesignIteration1Enabled) {
      return null;
    }

    const isDeferredPaymentMethodSelected = deferredPaymentMethod?.some(
      (paymentMethod) => paymentMethod?.id === invoice?.paymentMethodId
    );

    const hasPaymentIssue = doesInvoiceHasPaymentIssue(invoice, isDeferredPaymentMethodSelected);
    if (hasPaymentIssue) {
      return (
        <PaymentFailureBanner
          transactionAccountId={txaId}
          invoiceGroupId={invoice.billingGroupId}
          paymentMethodType={invoice.paymentMethod as any}
          dueDate={invoice.dueDate}
        />
      );
    }
    return null;
  };

  const renderAppPage = () => {
    const invoiceDetails = getInvoiceDetails();

    return (
      <>
        {isDesignIteration1Enabled || renderDownloadJSONBanner()}
        <AppPage className="app-page-v2 ppc-invoice-detail-page" allowedSidebars={allowedSidebars}>
          <ScreenEventAnalytics
            event={{
              name: AnalyticsEventSource.PARTNER_INVOICE_DETAILS_SCREEN,
              attributes: {},
            }}
          />
          <AppContent>
            {renderPaymentFailureBanner(invoiceDetails)}
            {renderInfoSection(invoiceDetails)}
            <section>
              <InvoiceDetailSummarySection
                billingDoc={invoiceDetails}
                transactionAccountId={txaId}
                shouldSkipFetchInHooks={shouldSkipFetchInHooks}
                hasPayByStatement={hasPayByStatement}
                isQuoteAndOrderNumbersEnabled={true}
                invoiceGroupId={data?.invoice?.billingGroupId}
                isDesignIteration1Enabled={isDesignIteration1Enabled}
                isPaypalEnabled={isPaypalEnabled}
                shouldShowACHPaymentMethod={isAchEnabled}
              />
            </section>
            {renderBillingProfileSection(invoiceDetails)}
            <div className="ppc-invoice-details-lineitem">{renderLineItems()}</div>
          </AppContent>
        </AppPage>
      </>
    );
  };

  const renderPage = () => {
    if (isDesignIteration1Enabled) {
      const breadcrumbsItems = [
        {
          title: 'Home',
          href: routePaths.home,
        },
        {
          title: 'Invoices',
          href: routePaths.invoices,
        },
        {
          title: invoice.invoiceNumber || 'Details',
          href: routePaths.invoiceDetails,
        },
      ];

      return (
        <>
          {renderDownloadJSONBanner()}
          <PageBackgroundV2
            heading={getPageHeading()}
            actions={renderActions()}
            className="ppc-invoice-details-v2"
            breadcrumbsProps={{ items: breadcrumbsItems }}
          >
            {renderAppPage()}
          </PageBackgroundV2>
        </>
      );
    }

    return (
      <PageBackground
        heading={getPageHeading()}
        imageKey={Background.INVOICE_DETAILS_BACKGROUND}
        className="page-background ppc-invoice-detail-background"
        isBackButtonEnabled={true}
        actions={renderActions()}
      >
        {renderAppPage()}
      </PageBackground>
    );
  };

  return (
    <TransactionAccountContext.Provider
      value={{
        getTransactionAccount,
        ...transactionAccountPageResult,
      }}
    >
      {renderPage()}
    </TransactionAccountContext.Provider>
  );
};
